import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Avatar,
  Button,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
  Autocomplete,
  TextField,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  Business as BusinessIcon,
  NavigateNext as NavigateNextIcon,
  NavigateBefore as NavigateBeforeIcon,
  Language as WebsiteIcon,
  LocationOn as LocationIcon,
  People as PeopleIcon,
  Work as WorkIcon,
  Person as PersonIcon,
  LinkOff as UnlinkIcon,
  AccountTree as GraphIcon,
} from '@mui/icons-material';
import { DashboardLayout } from '../layout/DashboardLayout';
import { useDatabase } from '../../hooks/useDatabase';
import { Organization, Contact } from '../../types/database';
import { OrganizationModal } from './OrganizationModal';
import { ContactModal } from '../contacts/ContactModal';

export const OrganizationDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const db = useDatabase();
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [allOrganizations, setAllOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [newContactModalOpen, setNewContactModalOpen] = useState(false);
  const [linkContactDialogOpen, setLinkContactDialogOpen] = useState(false);
  const [allContacts, setAllContacts] = useState<Contact[]>([]);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);

  useEffect(() => {
    const loadData = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        setError(null);
        
        // Load organization
        const { data: organizationData, error: organizationError } = await db.getOrganizationById(id);
        if (organizationError) throw organizationError;
        setOrganization(organizationData);

        // Load contacts linked to this organization
        const { data: linkedContactsData, error: contactsError } = await db.getOrganizationContacts(id);
        if (contactsError) throw contactsError;
        setContacts(linkedContactsData || []);
        
        // Load all contacts for linking dialog
        const { data: contactsData, error: allContactsError } = await db.getContacts();
        if (allContactsError) throw allContactsError;
        setAllContacts(contactsData || []);

        // Load all organizations for navigation and sort alphabetically
        const { data: organizationsData, error: organizationsError } = await db.getOrganizations();
        if (organizationsError) throw organizationsError;
        const sortedOrganizations = (organizationsData || []).sort((a, b) => 
          a.name.localeCompare(b.name)
        );
        setAllOrganizations(sortedOrganizations);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id, db]);

  const handleEdit = () => {
    setEditModalOpen(true);
  };

  const handleEditSubmit = async (formData: Omit<Organization, 'id' | 'created_at' | 'updated_at'>) => {
    if (!organization) return;
    
    try {
      const { error } = await db.updateOrganization(organization.id, formData);
      if (error) throw error;
      
      // Refresh organization data
      const { data: organizationData, error: organizationError } = await db.getOrganizationById(organization.id);
      if (organizationError) throw organizationError;
      setOrganization(organizationData);
      
      setEditModalOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update organization');
    }
  };

  const handleDelete = () => {
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!organization) return;
    
    try {
      const { error } = await db.deleteOrganization(organization.id);
      if (error) throw error;
      navigate('/organizations');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete organization');
    }
  };

  const handleCreateContact = async (contactData: Partial<Omit<Contact, 'id' | 'created_at' | 'updated_at' | 'relevance_score'>>, organizationId?: string | null) => {
    if (!organization) return;
    
    try {
      // Create contact
      const { data: newContact, error: createError } = await db.createContact(contactData);
      if (createError) throw createError;
      if (!newContact) throw new Error('Failed to create contact');
      
      // Link contact to organization (use provided organizationId or default to current organization)
      const orgIdToLink = organizationId || organization.id;
      if (orgIdToLink) {
        const { error: linkError } = await db.linkContactToOrganization(newContact.id, orgIdToLink);
        if (linkError) throw linkError;
      }
      
      // Refresh contacts list
      const { data: linkedContactsData, error: contactsError } = await db.getOrganizationContacts(organization.id);
      if (contactsError) throw contactsError;
      setContacts(linkedContactsData || []);
      
      // Refresh all contacts for linking dialog
      const { data: contactsData, error: allContactsError } = await db.getContacts();
      if (allContactsError) throw allContactsError;
      setAllContacts(contactsData || []);
      
      setNewContactModalOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create contact');
    }
  };

  const handleLinkContact = async () => {
    if (!organization || !selectedContact) return;

    try {
      // Link contact to organization
      const { error } = await db.linkContactToOrganization(selectedContact.id, organization.id);
      if (error) throw error;
      
      // Refresh contacts list
      const { data: linkedContactsData, error: contactsError } = await db.getOrganizationContacts(organization.id);
      if (contactsError) throw contactsError;
      setContacts(linkedContactsData || []);
      
      // Refresh all contacts for linking dialog
      const { data: contactsData, error: allContactsError } = await db.getContacts();
      if (allContactsError) throw allContactsError;
      setAllContacts(contactsData || []);
      
      setLinkContactDialogOpen(false);
      setSelectedContact(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to link contact');
    }
  };

  const handleUnlinkContact = async (contactId: string) => {
    if (!organization) return;

    try {
      // Unlink contact from organization
      const { error } = await db.unlinkContactFromOrganization(contactId, organization.id);
      if (error) throw error;
      
      // Refresh contacts list
      const { data: linkedContactsData, error: contactsError } = await db.getOrganizationContacts(organization.id);
      if (contactsError) throw contactsError;
      setContacts(linkedContactsData || []);
      
      // Refresh all contacts for linking dialog
      const { data: contactsData, error: allContactsError } = await db.getContacts();
      if (allContactsError) throw allContactsError;
      setAllContacts(contactsData || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to unlink contact');
    }
  };

  // Get current organization index and navigation functions
  const currentIndex = organization ? allOrganizations.findIndex(org => org.id === organization.id) : -1;
  const hasNext = currentIndex < allOrganizations.length - 1;
  const hasPrevious = currentIndex > 0;

  const handleNext = () => {
    if (hasNext && currentIndex !== -1) {
      const nextOrganization = allOrganizations[currentIndex + 1];
      navigate(`/organizations/${nextOrganization.id}`);
    }
  };

  const handlePrevious = () => {
    if (hasPrevious && currentIndex !== -1) {
      const prevOrganization = allOrganizations[currentIndex - 1];
      navigate(`/organizations/${prevOrganization.id}`);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <CircularProgress />
        </Box>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <Box sx={{ p: 2 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      </DashboardLayout>
    );
  }

  if (!organization) {
    return (
      <DashboardLayout>
        <Box sx={{ p: 2 }}>
          <Alert severity="warning">Organization not found</Alert>
        </Box>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Box sx={{ p: 3 }}>
        {/* Navigation and Actions */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton onClick={() => navigate('/organizations')}>
              <ArrowBackIcon />
            </IconButton>
            <IconButton onClick={handlePrevious} disabled={!hasPrevious}>
              <NavigateBeforeIcon />
            </IconButton>
            <IconButton onClick={handleNext} disabled={!hasNext}>
              <NavigateNextIcon />
            </IconButton>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton
              size="small"
              onClick={handleEdit}
              title="Edit Organization"
              sx={{ 
                border: theme => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)'}`,
                '& .MuiSvgIcon-root': {
                  color: theme => theme.palette.mode === 'dark' ? 'white' : 'black'
                }
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => navigate(`/graph?isolate=${organization.id}`)}
              title="View Isolated Network"
              sx={{ 
                border: theme => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)'}`,
                '& .MuiSvgIcon-root': {
                  color: theme => theme.palette.mode === 'dark' ? 'white' : 'black'
                }
              }}
            >
              <GraphIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              color="error"
              onClick={handleDelete}
              title="Delete Organization"
              sx={{ 
                border: theme => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(211, 47, 47, 0.5)' : 'rgba(211, 47, 47, 0.5)'}`,
                '& .MuiSvgIcon-root': {
                  color: theme => theme.palette.mode === 'dark' ? 'white' : 'error.main'
                }
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {/* Organization Information */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
                <Avatar
                  sx={{ width: 100, height: 100, mb: 2, bgcolor: 'primary.main' }}
                >
                  <BusinessIcon sx={{ fontSize: 48 }} />
                </Avatar>
                <Typography variant="h5" gutterBottom>
                  {organization.name}
                </Typography>
                {organization.industry && (
                  <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                    {organization.industry}
                  </Typography>
                )}
                {organization.size && (
                  <Typography variant="subtitle2" color="text.secondary">
                    {organization.size} employees
                  </Typography>
                )}
              </Box>

              <Divider sx={{ my: 2 }} />

              <List>
                {organization.website && (
                  <ListItem>
                    <ListItemIcon>
                      <WebsiteIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Website"
                      secondary={
                        <Link 
                          href={organization.website.startsWith('http') ? organization.website : `https://${organization.website}`}
                          target="_blank" 
                          rel="noopener noreferrer"
                        >
                          {organization.website}
                        </Link>
                      }
                    />
                  </ListItem>
                )}
                {organization.location && (
                  <ListItem>
                    <ListItemIcon>
                      <LocationIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Location"
                      secondary={organization.location}
                    />
                  </ListItem>
                )}
                {organization.industry && (
                  <ListItem>
                    <ListItemIcon>
                      <WorkIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Industry"
                      secondary={organization.industry}
                    />
                  </ListItem>
                )}
                {organization.size && (
                  <ListItem>
                    <ListItemIcon>
                      <PeopleIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Size"
                      secondary={organization.size}
                    />
                  </ListItem>
                )}
              </List>

              {organization.description && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" gutterBottom>
                    Description
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {organization.description}
                  </Typography>
                </>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12} md={8}>
            <Grid container spacing={3}>
              {/* Contacts Section */}
              <Grid item xs={12}>
                <Paper sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">Contacts</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        {contacts.length} contact{contacts.length !== 1 ? 's' : ''}
                      </Typography>
                      <Button
                        variant="outlined"
                        startIcon={<PersonIcon />}
                        onClick={() => setNewContactModalOpen(true)}
                        size="small"
                      >
                        Add Contact
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() => setLinkContactDialogOpen(true)}
                        size="small"
                      >
                        Link Existing
                      </Button>
                    </Box>
                  </Box>

                  {contacts.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <PersonIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                      <Typography variant="body2" color="text.secondary">
                        No contacts linked to this organization
                      </Typography>
                    </Box>
                  ) : (
                    <List>
                      {contacts.map((contact, index) => (
                        <React.Fragment key={contact.id}>
                          <ListItem
                            secondaryAction={
                              <IconButton
                                edge="end"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleUnlinkContact(contact.id);
                                }}
                                title="Unlink from organization"
                                color="error"
                              >
                                <UnlinkIcon />
                              </IconButton>
                            }
                            onClick={() => navigate(`/contacts/${contact.id}`)}
                            sx={{ 
                              cursor: 'pointer',
                              '&:hover': {
                                backgroundColor: theme => theme.palette.action.hover
                              }
                            }}
                          >
                            <ListItemIcon>
                              <PersonIcon />
                            </ListItemIcon>
                            <ListItemText
                              primary={contact.name}
                              secondary={
                                <Box>
                                  {contact.email && (
                                    <Typography variant="body2" color="text.secondary">
                                      {contact.email}
                                    </Typography>
                                  )}
                                  {contact.title && (
                                    <Typography variant="body2" color="text.secondary">
                                      {contact.title}
                                    </Typography>
                                  )}
                                </Box>
                              }
                            />
                          </ListItem>
                          {index < contacts.length - 1 && <Divider />}
                        </React.Fragment>
                      ))}
                    </List>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>

      {/* Modals and Dialogs */}
      <OrganizationModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        onSubmit={handleEditSubmit}
        organization={organization}
      />

      <ContactModal
        open={newContactModalOpen}
        onClose={() => setNewContactModalOpen(false)}
        onSubmit={handleCreateContact}
        contact={null}
      />

      <Dialog
        open={linkContactDialogOpen}
        onClose={() => setLinkContactDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Link Existing Contact</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Autocomplete
              options={allContacts.filter(contact => !contacts.some(c => c.id === contact.id))}
              getOptionLabel={(option) => option.name}
              value={selectedContact}
              onChange={(_, newValue) => setSelectedContact(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Contact"
                  placeholder="Search for a contact to link"
                />
              )}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkContactDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleLinkContact}
            disabled={!selectedContact}
            variant="contained"
          >
            Link Contact
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Organization</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {organization?.name}? This action cannot be undone.
            {contacts.length > 0 && (
              <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                Warning: This organization has {contacts.length} linked contact{contacts.length !== 1 ? 's' : ''}. 
                The organization link will be removed from these contacts.
              </Typography>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
};
